import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdSlot from "./ads/AdSlot";
import { ADSENSE_HOME_SLOT } from "./appConfig";
import CardTile from "./CardTile";
import { apiFetch } from "./api/client";
import { useAuth } from "./authContext";
import { useCurrentUser } from "./currentUserContext";
import { fetchMarketCards } from "./api/cards";
import { buildBrowseUserPath } from "./utils/browseUserPath";

const FEATURED_LISTINGS_LIMIT = 6;
const GOOD_DEALS_LIMIT = 6;
const HOME_MARKET_FETCH_LIMIT = 250;
const MIN_GOOD_DEAL_TREND_PRICE = 20;
const SHOW_HOME_SPONSORED_BANNER = false;
const DEFAULT_TRADE_FINDER_SETTINGS = Object.freeze({
  radiusKm: 0,
  socialEnabled: false,
  socialSelectedUserIds: [],
  locationLabel: null,
  locationLat: null,
  locationLng: null
});

function getCardSalePrice(card) {
  const value = Number(card?.sale_price ?? card?.user_price);
  return Number.isFinite(value) ? value : null;
}

function getCardTrendPrice(card) {
  const value = Number(card?.price_trend);
  return Number.isFinite(value) ? value : null;
}

function getDiscountAmount(card) {
  const salePrice = getCardSalePrice(card);
  const trendPrice = getCardTrendPrice(card);

  if (salePrice === null || trendPrice === null) {
    return null;
  }

  return trendPrice - salePrice;
}

function isGoodDeal(card) {
  const discountAmount = getDiscountAmount(card);
  const trendPrice = getCardTrendPrice(card);

  if (discountAmount === null || trendPrice === null || trendPrice < MIN_GOOD_DEAL_TREND_PRICE) {
    return false;
  }

  return discountAmount > 0;
}

function compareGoodDeals(leftCard, rightCard) {
  const leftDiscountAmount = getDiscountAmount(leftCard) || 0;
  const rightDiscountAmount = getDiscountAmount(rightCard) || 0;
  const leftSalePrice = getCardSalePrice(leftCard) || 0;
  const rightSalePrice = getCardSalePrice(rightCard) || 0;
  const leftTrendPrice = getCardTrendPrice(leftCard) || 0;
  const rightTrendPrice = getCardTrendPrice(rightCard) || 0;

  if (rightDiscountAmount !== leftDiscountAmount) {
    return rightDiscountAmount - leftDiscountAmount;
  }

  if (rightTrendPrice !== leftTrendPrice) {
    return rightTrendPrice - leftTrendPrice;
  }

  return leftSalePrice - rightSalePrice;
}

function compareFeaturedListings(leftCard, rightCard) {
  const leftSalePrice = getCardSalePrice(leftCard) || 0;
  const rightSalePrice = getCardSalePrice(rightCard) || 0;
  const leftTrendPrice = getCardTrendPrice(leftCard) || 0;
  const rightTrendPrice = getCardTrendPrice(rightCard) || 0;

  if (rightSalePrice !== leftSalePrice) {
    return rightSalePrice - leftSalePrice;
  }

  return rightTrendPrice - leftTrendPrice;
}

function getTraderLink(card) {
  const userId = Number(card?.user_id);

  if (!Number.isFinite(userId) || userId <= 0) {
    return null;
  }

  const username = String(card?.trader_username || `User ${userId}`);

  return {
    to: buildBrowseUserPath(userId, username),
    state: { userId, username, collectorTier: card?.trader_collector_tier || null },
    label: username,
    collectorTier: card?.trader_collector_tier || null
  };
}

function renderHomeCardGrid(cards, keyPrefix) {
  return (
    <div className="card-grid">
      {cards.map((card) => (
        <div
          className="card-grid__item home-view__card-grid-item"
          key={`${keyPrefix}-${card.id}`}
          style={{
            position: "relative",
            cursor: "default",
            borderRadius: "8px",
            transition: "0.15s",
            padding: "2px",
            minHeight: "var(--card-grid-item-height)",
            height: "auto",
            overflow: "visible"
          }}
        >
          <CardTile
            card={card}
            badgeType="LISTED"
            salePrice={card.sale_price ?? card.user_price}
            traderLink={getTraderLink(card)}
            preferNormalImage={true}
            showCustomPriceComparison
          />
        </div>
      ))}
    </div>
  );
}

function renderAnonymousListingsPrompt(listingLabel, authLinkStyle) {
  return (
    <p style={{ margin: 0, color: "#475569", fontWeight: 600 }}>
      <Link to="/auth" state={{ authMode: "register" }} style={authLinkStyle}>Sign up</Link>
      {" or "}
      <Link to="/auth" style={authLinkStyle}>log in</Link>
      {` to load ${listingLabel}.`}
    </p>
  );
}

function filterOwnListings(cards = [], selectedUserId = null) {
  const normalizedSelectedUserId = Number(selectedUserId);

  if (!Number.isFinite(normalizedSelectedUserId) || normalizedSelectedUserId <= 0) {
    return cards;
  }

  return cards.filter((card) => Number(card?.user_id) !== normalizedSelectedUserId);
}

function normalizeTradeFinderSettings(settings = {}) {
  const radiusKm = Number.parseInt(String(settings?.radiusKm ?? ""), 10);
  const locationLabel = String(settings?.locationLabel || "").trim();
  const locationLat = Number(settings?.locationLat);
  const locationLng = Number(settings?.locationLng);

  return {
    radiusKm: Number.isInteger(radiusKm) && radiusKm > 0 ? radiusKm : 0,
    socialEnabled: Boolean(settings?.socialEnabled),
    socialSelectedUserIds: Array.isArray(settings?.socialSelectedUserIds)
      ? settings.socialSelectedUserIds
          .map((entry) => Number.parseInt(String(entry ?? ""), 10))
          .filter((entry) => Number.isInteger(entry) && entry > 0)
      : [],
    locationLabel: locationLabel || null,
    locationLat: Number.isFinite(locationLat) ? locationLat : null,
    locationLng: Number.isFinite(locationLng) ? locationLng : null
  };
}

function hasTradeFinderLocation(settings = {}) {
  return Boolean(
    String(settings?.locationLabel || "").trim()
      && Number.isFinite(Number(settings?.locationLat))
      && Number.isFinite(Number(settings?.locationLng))
  );
}

function toRadians(value) {
  return (Number(value) * Math.PI) / 180;
}

function calculateDistanceKm(fromSettings = {}, toSettings = {}) {
  if (!hasTradeFinderLocation(fromSettings) || !hasTradeFinderLocation(toSettings)) {
    return null;
  }

  const earthRadiusKm = 6371;
  const latitudeDistance = toRadians(Number(toSettings.locationLat) - Number(fromSettings.locationLat));
  const longitudeDistance = toRadians(Number(toSettings.locationLng) - Number(fromSettings.locationLng));
  const fromLatitudeRadians = toRadians(Number(fromSettings.locationLat));
  const toLatitudeRadians = toRadians(Number(toSettings.locationLat));
  const haversineValue = Math.sin(latitudeDistance / 2) ** 2
    + Math.cos(fromLatitudeRadians) * Math.cos(toLatitudeRadians) * Math.sin(longitudeDistance / 2) ** 2;
  const angularDistance = 2 * Math.atan2(Math.sqrt(haversineValue), Math.sqrt(1 - haversineValue));

  return earthRadiusKm * angularDistance;
}

function getTraderTradeFinderSettings(card = {}) {
  return normalizeTradeFinderSettings({
    locationLabel: card?.trader_location_label,
    locationLat: card?.trader_location_lat,
    locationLng: card?.trader_location_lng
  });
}

function passesTradeFinderFilters(card, tradeFinderSettings = DEFAULT_TRADE_FINDER_SETTINGS) {
  const normalizedSettings = normalizeTradeFinderSettings(tradeFinderSettings);

  if (normalizedSettings.socialEnabled) {
    if (normalizedSettings.socialSelectedUserIds.length === 0) {
      return false;
    }

    if (!normalizedSettings.socialSelectedUserIds.includes(Number(card?.user_id))) {
      return false;
    }
  }

  if (normalizedSettings.radiusKm <= 0) {
    return true;
  }

  if (!hasTradeFinderLocation(normalizedSettings)) {
    return false;
  }

  const distanceKm = calculateDistanceKm(normalizedSettings, getTraderTradeFinderSettings(card));

  return distanceKm !== null && distanceKm <= normalizedSettings.radiusKm;
}

export default function HomeView() {
  const { isAuthenticated } = useAuth();
  const { selectedUserId } = useCurrentUser();
  const [featuredCards, setFeaturedCards] = useState([]);
  const [discountedCards, setDiscountedCards] = useState([]);
  const [featuredError, setFeaturedError] = useState(null);
  const [discountedError, setDiscountedError] = useState(null);

  const panelStyle = {
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbe4f0",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)"
  };

  const authLinkStyle = {
    color: "#0f766e",
    fontWeight: 700,
    textDecoration: "none"
  };

  useEffect(() => {
    let isDisposed = false;

    const tradeFinderSettingsPromise = isAuthenticated
      ? apiFetch(`/api/account/tradefinder-settings/${selectedUserId}`, { userId: selectedUserId })
          .then(async (response) => {
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
              throw new Error(data.error || "Could not load TradeFinder settings");
            }

            return normalizeTradeFinderSettings(data?.settings || DEFAULT_TRADE_FINDER_SETTINGS);
          })
          .catch((error) => {
            console.error("Home TradeFinder settings load error:", error);
            return DEFAULT_TRADE_FINDER_SETTINGS;
          })
      : Promise.resolve(DEFAULT_TRADE_FINDER_SETTINGS);

    Promise.all([
      fetchMarketCards({ page: 1, limit: HOME_MARKET_FETCH_LIMIT, sortBy: "price", sortDirection: "desc" }),
      fetchMarketCards({ page: 1, limit: HOME_MARKET_FETCH_LIMIT, sortBy: "trend", sortDirection: "desc" }),
      tradeFinderSettingsPromise
    ])
      .then(([featuredData, discountedData, tradeFinderSettings]) => {
        if (isDisposed) {
          return;
        }

        const featuredNearbyCards = filterOwnListings(Array.isArray(featuredData?.cards) ? featuredData.cards : [], selectedUserId)
          .filter((card) => passesTradeFinderFilters(card, tradeFinderSettings))
          .sort(compareFeaturedListings)
          .slice(0, FEATURED_LISTINGS_LIMIT);

        setFeaturedCards(featuredNearbyCards);
        setFeaturedError(null);

        const filteredDiscountedCards = filterOwnListings(Array.isArray(discountedData?.cards) ? discountedData.cards : [], selectedUserId)
          .filter((card) => isGoodDeal(card) && passesTradeFinderFilters(card, tradeFinderSettings))
          .sort(compareGoodDeals)
          .slice(0, GOOD_DEALS_LIMIT);

        setDiscountedCards(filteredDiscountedCards);
        setDiscountedError(null);
      })
      .catch(() => {
        if (isDisposed) {
          return;
        }

        setFeaturedCards([]);
        setDiscountedCards([]);
        setFeaturedError("Could not load featured listings.");
        setDiscountedError("Could not load discounted listings.");
      });

    return () => {
      isDisposed = true;
    };
  }, [isAuthenticated, selectedUserId]);

  return (
    <div className="app-responsive-shell home-view">
      <section className="home-view__hero">
        <div className="home-view__hero-row">
          <div className="home-view__hero-copy">
            <h1 className="home-view__title">Find and Trade Magic: the Gathering cards</h1>
            <p className="home-view__intro">
              Connect with other Magic: The Gathering collectors. List your traders and wanted cards. Adjust your TradeFinder settings to find trades in your area with the TradeFinder Tool.
            </p>
          </div>
        </div>
      </section>

      {SHOW_HOME_SPONSORED_BANNER ? (
        <AdSlot
          slot={ADSENSE_HOME_SLOT}
          label="Sponsored"
          className="home-view__ad-slot"
          style={{ minHeight: "120px" }}
        />
      ) : null}

      <section className="home-view__layout">
        <div className="home-view__listing-grid">
          <div className="home-view__main-panel home-view__listing-panel" style={panelStyle}>
            <div className="home-view__section-header">
              <div>
                <div className="home-view__eyebrow">
                  Featured listings (Near you)
                </div>
                <div className="home-view__section-copy">
                  A small selection of cards currently listed for trade within your TradeFinder area.
                </div>
              </div>
            </div>

            {featuredError ? (
              !isAuthenticated
                ? renderAnonymousListingsPrompt("featured listings", authLinkStyle)
                : <p style={{ margin: 0, color: "#b91c1c", fontWeight: 600 }}>{featuredError}</p>
            ) : null}

            {!featuredError && featuredCards.length === 0 ? (
              <p style={{ margin: 0, color: "#64748b" }}>No active listings yet.</p>
            ) : null}

            {!featuredError && featuredCards.length > 0 ? (
              renderHomeCardGrid(featuredCards, "featured")
            ) : null}
          </div>

          <div className="home-view__main-panel home-view__listing-panel" style={panelStyle}>
            <div className="home-view__section-header">
              <div>
                <div className="home-view__eyebrow">
                  Discounted listings (Near you)
                </div>
                <div className="home-view__section-copy">
                  Cards listed significantly under their recent market trend price within your area.
                </div>
              </div>
            </div>

            {discountedError ? (
              !isAuthenticated
                ? renderAnonymousListingsPrompt("discounted listings", authLinkStyle)
                : <p style={{ margin: 0, color: "#b91c1c", fontWeight: 600 }}>{discountedError}</p>
            ) : null}

            {!discountedError && discountedCards.length === 0 ? (
              <p style={{ margin: 0, color: "#64748b" }}>No active listings yet.</p>
            ) : null}

            {!discountedError && discountedCards.length > 0 ? (
              renderHomeCardGrid(discountedCards, "discounted")
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
